import api from './axiosInstance';

export type WorkLibraryKind = 'education' | 'safety' | 'qaqc' | 'manpower' | 'machine';
export type WorkLibraryVisibility = 'org' | 'private';

export interface WorkLibraryItem {
  id: string;
  kind: WorkLibraryKind;
  label: string;
  /** @deprecated Use payload instead — kept for backward compat with existing safety/qaqc items. */
  workInstruction?: string;
  disciplineKey: string | null;
  disciplineLabel: string | null;
  active: boolean;
  visibility: WorkLibraryVisibility;
  /** Normalized by the backend list endpoint — private-item owner chip. */
  createdBy?: { id: string; name: string };
  /** Structured gate payload — agendaTopics/briefing/eduType for education;
   *  permitType/hazards/ppe/precautions for safety; behavior/acceptanceCriteria for qaqc;
   *  skillLevel for manpower. */
  payload?: Record<string, unknown>;
}

export interface ListWorkLibraryParams {
  kind: WorkLibraryKind;
  disciplineKey?: string | null;
  includeInactive?: boolean;
}

export interface CreateWorkLibraryInput {
  kind: WorkLibraryKind;
  label: string;
  /** @deprecated Use payload instead. Still accepted by the backend for safety/qaqc. */
  workInstruction?: string;
  disciplineKey?: string | null;
  disciplineLabel?: string | null;
  visibility?: WorkLibraryVisibility;
  /** Structured gate payload. Pass workInstruction here for legacy safety/qaqc items. */
  payload?: Record<string, unknown>;
}

export interface UpdateWorkLibraryInput {
  label?: string;
  /** @deprecated Use payload instead. Still accepted by the backend for safety/qaqc. */
  workInstruction?: string;
  disciplineKey?: string | null;
  disciplineLabel?: string | null;
  active?: boolean;
  visibility?: WorkLibraryVisibility;
  payload?: Record<string, unknown>;
}

// The backend sends raw `{ items }` / `{ item }`; the axios response interceptor
// wraps legacy bodies as `{ success, message, data }`, so payloads live at r.data.data.

export async function listWorkLibrary(params: ListWorkLibraryParams): Promise<WorkLibraryItem[]> {
  const query: Record<string, string> = { kind: params.kind };
  if (params.disciplineKey !== undefined) query['disciplineKey'] = params.disciplineKey ?? '';
  if (params.includeInactive) query['includeInactive'] = 'true';
  const r = await api.get('/epc/work-library', { params: query });
  return r.data.data.items as WorkLibraryItem[];
}

export async function createWorkLibraryItem(input: CreateWorkLibraryInput): Promise<WorkLibraryItem> {
  const r = await api.post('/epc/work-library', input);
  return r.data.data.item as WorkLibraryItem;
}

export async function updateWorkLibraryItem(id: string, input: UpdateWorkLibraryInput): Promise<WorkLibraryItem> {
  const r = await api.patch(`/epc/work-library/${id}`, input);
  return r.data.data.item as WorkLibraryItem;
}

export async function deactivateWorkLibraryItem(id: string): Promise<WorkLibraryItem> {
  const r = await api.delete(`/epc/work-library/${id}`);
  return r.data.data.item as WorkLibraryItem;
}
